import { useQuery } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { useCategories } from "@/hooks/useCategories";

/**
 * Iter-219 nav — admin-managed category order + quick-link pills bar.
 * Contract: `GET /api/classifieds/nav` → `{country, categories[], pills[]}`
 *
 * Resilience (added after iter-219 mobile screenshot review): if the nav
 * endpoint 404s, times out, or returns empty in a stale preview env, we
 * fall back to the plain `/api/classifieds/categories` payload so the header
 * never renders empty. The pills bar hides itself when the fallback kicks
 * in (no admin-curated pills → nothing to show).
 */
export default function useNav() {
  const cats = useCategories();
  const nav = useQuery({
    queryKey: ["classifieds-nav"],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/nav", { params: { country: "NZ" } });
      return { categories: data?.categories || [], pills: data?.pills || [] };
    },
    staleTime: 15_000,
    refetchOnWindowFocus: true,
    refetchInterval: 120_000,
    retry: 0,
  });

  const navCats = nav.data?.categories || [];
  const navPills = nav.data?.pills || [];
  const usingFallback = navCats.length === 0 && (cats.data || []).length > 0;

  const categories = usingFallback
    ? // Adapt the browse tree to the nav shape (label mirrors name, no badges).
      (cats.data || []).map((c) => ({
        id: c.id,
        slug: c.slug,
        name: c.name,
        label: c.name,
        type: c.type || "ad",
        badge: null,
        thumbnail_url: c.thumbnail_url,
        children: (c.children || []).map((sc) => ({
          id: sc.id,
          slug: sc.slug,
          name: sc.name,
          badge: null,
        })),
      }))
    : navCats;

  return {
    ...nav,
    data: { categories, pills: navPills },
    usingFallback,
  };
}
